import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { IMPORT_STATE, type ImportRuleT, type IntercomInfo } from '../types';

const normalizeIntercomWorkspace = (data: any): IntercomInfo | null => {
  if (!data || data.length < 1) return null;

  const workspace = data[0];
  if (!workspace?.alias) return null;

  return {
    alias: workspace.alias,
    workspace_id: workspace.workspace_id ?? '',
    import_state: workspace.state ?? IMPORT_STATE.NOT_STARTED,
    history: workspace.history ?? [],
    name: workspace.name ?? 'Intercom workspace',
    import_rule: workspace.import_rule ?? {
      status: 1,
      bound: 1,
      tag: 1,
      tag_names: [],
    },
  };
};

export const useIntercom = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(SOURCE_TYPE.INTERCOM, workspaceAlias);

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => {
      const response = await service.intercom.getIntercomInfo(workspaceAlias);
      return normalizeIntercomWorkspace(response);
    },
    enabled,
  });

  const { mutateAsync: disconnectMutation } = useMutation({
    mutationFn: async () => {
      const intercomWorkspace = queryClient.getQueryData(queryKeys) as IntercomInfo | null;
      if (!intercomWorkspace?.alias) return;
      await service.intercom.deleteIntercomInfo(intercomWorkspace.alias);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutateAsync: requestImportMutation } = useMutation({
    mutationFn: async ({ start_date, end_date }: NullableDateRangeT) => {
      if (!start_date || !end_date) {
        throw new Error('Date range is required');
      }
      await service.intercom.importWorkspaceFeedback(
        workspaceAlias,
        start_date,
        end_date,
      );
    },
    onSuccess: () => {
      const intercomWorkspace = queryClient.getQueryData(queryKeys) as IntercomInfo | null;
      if (!intercomWorkspace) return;

      queryClient.setQueryData(queryKeys, {
        ...intercomWorkspace,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  const saveImportRule = (importRule: ImportRuleT) => {
    queryClient.setQueryData(queryKeys, (oldData: IntercomInfo | null) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        import_rule: importRule,
      };
    });
  };

  const { mutateAsync: updateImportOptionMutation } = useMutation({
    mutationFn: async (importRule: ImportRuleT) => {
      await service.intercom.updateIntercomImportOption(importRule);
      return importRule;
    },
    onSuccess: saveImportRule,
  });

  const { mutateAsync: updateTriggerOptionMutation } = useMutation({
    mutationFn: async (importRule: ImportRuleT) => {
      await service.intercom.updateIntercomImportOption(importRule);
      return importRule;
    },
    onSuccess: saveImportRule,
  });

  return {
    intercomWorkspace: data ?? null,
    disconnectMutation,
    requestImportMutation,
    updateImportOptionMutation,
    updateTriggerOptionMutation,
    invalidateIntercomQuery: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys,
      }),
  };
};
